/* eslint-disable */
import React, { useMemo } from 'react';
import { useUiStore, DEFAULT_DEV_VIEW } from '../../stores/uiStore';
import { useProjectsUiStore } from '../../portals/projects/projectsUiStore';
import { useOrdersUiStore } from '../../portals/orders/ordersUiStore';
import { PORTAL_NAV } from '../../constants/portals';
import { PORTAL_META, ORDERS_WORLDS } from './portalMeta';
import FilterBar from './FilterBar';
import { useSubsectionCounts } from '../../hooks/useSubsectionCounts';

// ============================================================
// GenericBar — the filter bar's identity row for a portal that has no
// dedicated v3 adapter yet (S3 Originals · S5 Orders · S6 QC/Sales/Projects
// replace these one by one): portal › section, the Orders world switcher,
// and the LIVE slot. Sections drive the same store the mega-menu drives.
// ============================================================

export default function GenericBar({ portalKey, tab, setTab, role, canSeeMailIntel, isAdmin, condensed, chromeProps, deskReady, published }) {
  const devView = useUiStore((s) => s.devView);
  const setDevView = useUiStore((s) => s.setDevView);
  const setAnalyticsView = useUiStore((s) => s.setAnalyticsView);
  const setSettingsView = useUiStore((s) => s.setSettingsView);
  const setSettingsPanel = useUiStore((s) => s.setSettingsPanel);
  const salesView = useUiStore((s) => s.salesView);
  const setSalesView = useUiStore((s) => s.setSalesView);
  const projectsView = useProjectsUiStore((s) => s.projectsView);
  const setProjectsView = useProjectsUiStore((s) => s.setProjectsView);
  const world = useOrdersUiStore((s) => s.world);
  const ordersView = useOrdersUiStore((s) => s.ordersView);
  const deskView = useOrdersUiStore((s) => s.deskView);
  const collectionView = useOrdersUiStore((s) => s.collection.view);

  const portal = PORTAL_NAV.find((p) => p.key === portalKey);
  const meta = PORTAL_META[portalKey] || { icon: 'folder', short: portalKey };
  const isOrders = portalKey === 'orders';

  const { data: subCounts } = useSubsectionCounts(true);
  const cnt = (k) => { const v = subCounts && subCounts[portalKey] ? subCounts[portalKey][k] : null; return v == null ? undefined : v; };
  const { sections, section, onSection } = useMemo(() => {
    if (isOrders) {
      const w = ORDERS_WORLDS.find((x) => x.key === world);
      const cur = world === 'book' ? ordersView : world === 'desk' ? deskView : world === 'collection' ? collectionView : null;
      return {
        sections: w ? w.sections.map((s) => ({ key: s.key, label: s.label })) : [],
        section: cur,
        onSection: (k) => { const st = useOrdersUiStore.getState(); if (world === 'book') st.setOrdersView(k); else if (world === 'desk') st.setDeskView(k); else st.setCollection({ view: k }); },
      };
    }
    const subs = (portal?.subsections || []).filter((s) => (!s.adminOnly || isAdmin) && (!s.miGated || canSeeMailIntel));
    if (portalKey === 'developments') return { sections: subs.map((s) => ({ key: s.devView, label: s.label, count: cnt(s.key) })), section: devView, onSection: (k) => { setAnalyticsView(null); setSettingsView(null); setSettingsPanel(null); setDevView(k || DEFAULT_DEV_VIEW); } };
    if (portalKey === 'sales') return { sections: subs.map((s) => ({ key: s.salesView, label: s.label, count: cnt(s.key) })), section: salesView, onSection: (k) => setSalesView(k) };
    if (portalKey === 'projects') return { sections: subs.map((s) => ({ key: s.projectsView, label: s.label, count: cnt(s.key) })), section: projectsView, onSection: (k) => setProjectsView(k) };
    if (portalKey === 'catalog') return { sections: subs.map((s) => ({ key: s.tab, label: s.label, count: cnt(s.key) })), section: tab, onSection: (k) => setTab(k) };
    return { sections: [], section: null, onSection: () => {} };
  }, [portalKey, isOrders, world, ordersView, deskView, collectionView, devView, salesView, projectsView, tab, isAdmin, canSeeMailIntel, subCounts]);

  const base = {
    portal: { key: portalKey, name: meta.short || portal?.label || portalKey, icon: meta.icon },
    worlds: isOrders ? ORDERS_WORLDS.map((w) => ({ key: w.key, label: w.label, icon: w.icon, disabled: w.key === 'desk' && deskReady === false, title: w.key === 'desk' && deskReady === false ? 'The Order Desk opens once its SQL package has run' : w.label })) : undefined,
    world,
    onWorld: (w) => { const st = useOrdersUiStore.getState(); st.setWorld(w); try { localStorage.setItem('clb.orders.world', w); } catch (e) {} },
    sections, section, onSection,
    status: [{ dot: 'live', text: 'Live' }],
    fields: [],
  };
  // S6 — a portal that published its own bar (useShellBar) wins on every key
  // it set; the identity row stays the shell's unless the portal owns its
  // sections (QC's tabs live in QcPortal state, not a store).
  const cfg = published ? { ...base, ...published, portal: { ...base.portal, ...(published.portal || {}) } } : base;
  return <FilterBar cfg={cfg} condensed={condensed} {...chromeProps} />;
}
